import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, Timestamp, getDoc, doc, query, where, updateDoc, deleteDoc } from 'firebase/firestore';
import { Calendar, Clock, AlertCircle, Check, Sun, User, Play, Pause, X } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isSameMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import '../MonthViewMobile.css';
import EnhancedAppointmentDetailsModal from './EnhancedAppointmentDetailsModal';
import EnhancedEditAppointmentModal from './EnhancedEditAppointmentModal';
import { useTheme } from '../contexts/ThemeContext';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';

const MonthViewMobile = ({ db, currentDate, currentUser, onDateChange, showCompletedAppointments }) => {
  const { theme } = useTheme();

  // Vérifier si currentUser est null et retourner un message d'attente
  if (!currentUser || !currentUser.userId) {
    return (
      <div className="month-view-mobile flex flex-col h-screen">
        <div className="text-center text-gray-500 p-4">
          <p>Chargement des informations utilisateur...</p>
        </div>
      </div>
    );
  }

  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [currentMonth, setCurrentMonth] = useState(currentDate);
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [userPermissions, setUserPermissions] = useState({
    read: false,
    write: false,
    create: false,
    delete: false,
    manage: false
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailAppointment, setSelectedDetailAppointment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEditAppointment, setSelectedEditAppointment] = useState(null);
  const [clickTimer, setClickTimer] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedUnassignedAppointment, setSelectedUnassignedAppointment] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedInfoAppointment, setSelectedInfoAppointment] = useState(null);
  const clickDelay = 200; // Délai en millisecondes pour détecter un double-clic

  // Fonction pour récupérer les permissions de l'utilisateur
  const fetchUserPermissions = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;

        // Essayer d'abord le nouveau système advanced_roles
        try {
          const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
          if (advancedRoleDoc.exists()) {
            const roleData = advancedRoleDoc.data();
            const appointmentsPerms = roleData.permissions?.appointments || {};
            setUserPermissions({
              read: appointmentsPerms.read || false,
              write: appointmentsPerms.write || false,
              create: appointmentsPerms.create || false,
              delete: appointmentsPerms.delete || false,
              manage: appointmentsPerms.manage || false
            });
            return;
          }
        } catch (advancedError) {
          console.log("Nouveau système de permissions non disponible, utilisation du fallback...");
        }

        // Fallback: ancien système
        const roleDoc = await getDoc(doc(db, 'roles', userRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          setUserPermissions({
            read: roleData.permissions.includes('read_appointments'),
            write: roleData.permissions.includes('write_appointments'),
            create: roleData.permissions.includes('write_appointments'),
            delete: roleData.permissions.includes('write_appointments'),
            manage: roleData.permissions.includes('write_appointments')
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des permissions de l'utilisateur:", error);
    }
  }, [db, currentUser]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  // Générer les jours du mois avec les jours de remplissage
  const monthDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);

    // Calculer le premier jour à afficher (lundi de la semaine contenant le 1er du mois)
    const firstDayOfWeek = new Date(start);
    const dayOfWeek = firstDayOfWeek.getDay(); // 0 = dimanche, 1 = lundi, etc.
    // Ajuster pour que lundi soit le premier jour (soustraire 1 si lundi, 6 si dimanche, etc.)
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    firstDayOfWeek.setDate(firstDayOfWeek.getDate() - daysToSubtract);

    // Calculer le dernier jour à afficher (dimanche de la semaine contenant le dernier du mois)
    const lastDayOfWeek = new Date(end);
    const lastDayOfWeekDay = lastDayOfWeek.getDay(); // 0 = dimanche, 1 = lundi, etc.
    // Ajuster pour que dimanche soit le dernier jour
    const daysToAdd = lastDayOfWeekDay === 0 ? 0 : 7 - lastDayOfWeekDay;
    lastDayOfWeek.setDate(lastDayOfWeek.getDate() + daysToAdd);

    return eachDayOfInterval({ start: firstDayOfWeek, end: lastDayOfWeek });
  }, [currentMonth]);

  // Récupérer les employés
  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('isArchived', '==', false));

      // Promesses parallèles : liste filtrée ET utilisateur courant spécifique
      const [querySnapshot, currentUserDoc] = await Promise.all([
        getDocs(q),
        currentUser ? getDoc(doc(db, 'employees', currentUser.userId)) : Promise.resolve(null)
      ]);

      // Map des employés filtrés
      const fetchedEmployeesMap = new Map();
      querySnapshot.docs.forEach(doc => {
        fetchedEmployeesMap.set(doc.id, {
          userId: doc.id,
          ...doc.data(),
          color: doc.data().color || '#808080'
        });
      });

      // AJOUT FORCE DE L'UTILISATEUR COURANT s'il existe et n'est pas déjà présent
      if (currentUserDoc && currentUserDoc.exists()) {
        const userData = currentUserDoc.data();
        fetchedEmployeesMap.set(currentUserDoc.id, {
          userId: currentUserDoc.id,
          ...userData,
          color: userData.color || '#808080'
        });
      }

      setEmployees(Array.from(fetchedEmployeesMap.values()));
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [db, currentUser]);

  // Récupérer les rendez-vous du mois
  const fetchAppointments = useCallback(async () => {
    if (!db) return;
    try {
      const startOfMonthDate = startOfMonth(currentMonth);
      const endOfMonthDate = endOfMonth(currentMonth);

      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('date', '>=', Timestamp.fromDate(startOfMonthDate)),
        where('date', '<=', Timestamp.fromDate(endOfMonthDate))
      );

      const querySnapshot = await getDocs(q);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));

      const appointmentsWithClientInfo = await Promise.all(fetchedAppointments.map(async appointment => {
        const clientDoc = await getDoc(doc(db, 'clients', appointment.clientId));
        const clientData = clientDoc.data();
        return {
          ...appointment,
          clientName: clientData ? `${clientData.lastName} ${clientData.firstName}` : 'Client non spécifié',
          clientColor: clientData ? clientData.color : '#808080',
          clientAddress: clientData ? clientData.address : '',
          clientPhone: clientData ? clientData.phoneNumber : '',
          clientEmail: clientData ? clientData.email : '',
          status: appointment.status || 'libre'
        };
      }));

      setAppointments(appointmentsWithClientInfo);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous:", error);
    }
  }, [db, currentMonth]);

  // Récupérer les clients
  const fetchClients = useCallback(async () => {
    if (!db) return;
    try {
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const fetchedClients = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(fetchedClients);
    } catch (error) {
      console.error("Erreur lors de la récupération des clients:", error);
    }
  }, [db]);

  useEffect(() => {
    fetchEmployees();
    fetchAppointments();
    fetchClients();
  }, [fetchEmployees, fetchAppointments, fetchClients]);

  // Synchroniser les dates quand currentDate change (bouton "Aujourd'hui")
  useEffect(() => {
    setSelectedDate(currentDate);
    setCurrentMonth(currentDate);
  }, [currentDate]);

  // Rendez-vous du jour sélectionné (utilisateur connecté uniquement)
  const dayAppointments = useMemo(() => {
    return appointments
      .filter(app => isSameDay(app.date, selectedDate))
      .filter(app => app.employeeId === currentUser.userId) // Uniquement l'utilisateur connecté
      .filter(app => showCompletedAppointments || app.status !== 'completed') // Filtrer les rendez-vous terminés
      .sort((a, b) => {
        const aTime = a.startTime.split(':').map(Number);
        const bTime = b.startTime.split(':').map(Number);
        return (aTime[0] * 60 + aTime[1]) - (bTime[0] * 60 + bTime[1]);
      });
  }, [appointments, selectedDate, currentUser.userId, showCompletedAppointments]);

  // Plages à combler (rendez-vous non assignés)
  const sortedUnassignedAppointments = useMemo(() => {
    return appointments
      .filter(app => isSameDay(app.date, selectedDate))
      .filter(app => !app.employeeId || app.status === 'libre' || app.status === 'reserve')
      .filter(app => showCompletedAppointments || app.status !== 'completed')
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [appointments, selectedDate, showCompletedAppointments]);

  // Nombre de rendez-vous par jour (uniquement ceux de l'utilisateur)
  const getAppointmentsCount = useCallback((date) => {
    return appointments
      .filter(app => isSameDay(app.date, date))
      .filter(app => app.employeeId === currentUser.userId).length;
  }, [appointments, currentUser.userId]);





  // Composant pour le calendrier mensuel
  const MobileMonthCalendar = () => (
    <div className="calendar-grid p-4">
      {/* En-tête des jours de la semaine */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, index) => (
          <div key={index} className={`text-center text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} py-1`}>
            {day}
          </div>
        ))}
      </div>

      {/* Grille des jours */}
      <div className="grid grid-cols-7 gap-1">
        {monthDays.map((date, index) => {
          const appointmentsCount = getAppointmentsCount(date);
          const isSelected = isSameDay(date, selectedDate);
          const isCurrentDay = isToday(date);
          const isOtherMonth = !isSameMonth(date, currentMonth);
          const isHoliday = appointmentsCount === 0 && !isOtherMonth && isSameMonth(date, currentMonth);

          return (
            <button
              key={index}
              onClick={() => setSelectedDate(date)}
              className={`
                calendar-day
                ${isSelected ? 'selected' : ''}
                ${isCurrentDay ? 'today' : ''}
                ${isOtherMonth ? 'other-month' : ''}
                ${isHoliday ? 'holiday' : ''}
              `}
              title={isHoliday ? "Jour de congé" : ""}
            >
              <div className="font-medium">{format(date, 'd')}</div>

              {/* Indicateurs de rendez-vous - autant de points que de RDV */}
              {appointmentsCount > 0 && (
                <div className="appointment-indicators">
                  {Array.from({ length: Math.min(appointmentsCount, 3) }, (_, index) => (
                    <div
                      key={index}
                      className={`
                        appointment-indicator
                        ${isSelected ? 'selected' : ''}
                        appointment-indicator-${index + 1}
                      `}
                    />
                  ))}
                  {appointmentsCount > 3 && (
                    <div className={`
                      appointment-indicator-more
                      ${isSelected ? 'selected' : ''}
                    `}>
                      +{appointmentsCount - 3}
                    </div>
                  )}
                </div>
              )}

              {/* Indicateur de congé (soleil) pour les jours sans RDV - Toujours visible */}
              {isHoliday && !isOtherMonth && (
                <div className="holiday-indicator">
                  <Sun size={14} strokeWidth={2} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Composant pour l'affichage des rendez-vous du jour
  const MobileDayAppointments = () => (
    <div className="day-appointments">
      <div className="day-header">
        <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
          {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
        </h3>
        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          {dayAppointments.length} rendez-vous
        </p>
      </div>

      <div className="p-4">
        {/* Section des plages à combler - comme dans DayViewMobile et WeekViewMobile */}
        {sortedUnassignedAppointments.length > 0 && (
          <div className="wvm-unassigned-appointments mb-6">
            <h3 className="wvm-unassigned-title flex items-center">
              <AlertCircle className="mr-2 text-yellow-500" size={20} />
              Plages à combler :
            </h3>
            <div className="wvm-unassigned-list flex overflow-x-auto">
              {sortedUnassignedAppointments.map((appointment, index) => (
                <div
                  key={appointment.id}
                  className="mr-3"
                  style={{
                    width: '200px',
                    flexShrink: 0
                  }}
                  onClick={(event) => {
                    console.log("Clic sur plage à combler", appointment.status);
                    event.preventDefault();
                    event.stopPropagation();

                    if (appointment.status === 'libre' && (hasPermission('write') || hasPermission('read'))) {
                      console.log("Ouverture modal réservation");
                      setSelectedUnassignedAppointment(appointment);
                      setShowBookingModal(true);
                    } else if (appointment.status === 'reserve') {
                      console.log("Ouverture modal information");
                      setSelectedInfoAppointment(appointment);
                      setShowInfoModal(true);
                    }
                  }}
                >
                  <MobileAppointmentCard
                    appointment={appointment}
                    employees={employees}
                    onAppointmentClick={() => { }} // Fonction vide pour éviter les conflits
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section des rendez-vous de l'utilisateur */}
        {dayAppointments.length === 0 ? (
          <div className="empty-state">
            <Calendar className={`mx-auto ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} mb-2`} size={48} />
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Aucun rendez-vous ce jour</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dayAppointments.map((appointment) => (
              <MobileAppointmentCard
                key={appointment.id}
                appointment={appointment}
                employees={employees}
                onAppointmentClick={(event) => handleAppointmentClick(appointment, event)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );


  // Fonction pour mettre à jour un rendez-vous
  const handleAppointmentUpdate = (updatedAppointment) => {
    setAppointments(prevAppointments =>
      prevAppointments.map(app =>
        app.id === updatedAppointment.id ? updatedAppointment : app
      )
    );
  };

  // Fonction pour vérifier si l'utilisateur a une permission spécifique
  const hasPermission = useCallback((action) => userPermissions[action], [userPermissions]);

  // Fonction pour gérer les clics sur les rendez-vous (simple clic/double-clic)
  const handleAppointmentClick = (appointment, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (clickTimer) {
      // Double-clic détecté
      clearTimeout(clickTimer);
      setClickTimer(null);
      handleAppointmentDoubleClick(appointment);
    } else {
      // Définir un timer pour le simple clic
      const timer = setTimeout(() => {
        handleSingleClick(appointment);
        setClickTimer(null);
      }, clickDelay);
      setClickTimer(timer);
    }
  };

  const handleSingleClick = (appointment) => {
    setSelectedDetailAppointment(appointment);
    setShowDetailsModal(true);
  };

  const handleAppointmentDoubleClick = (appointment) => {
    if (hasPermission('write')) {
      setSelectedEditAppointment(appointment);
      setShowEditModal(true);
    }
  };

  const handleUpdateAppointment = async (updatedAppointment) => {
    if (!hasPermission('write')) return;

    try {
      const appointmentRef = doc(db, 'appointments', updatedAppointment.id);
      await updateDoc(appointmentRef, updatedAppointment);
      fetchAppointments();
      setShowEditModal(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous:", error);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!hasPermission('delete')) return;

    try {
      await deleteDoc(doc(db, 'appointments', appointmentId));
      fetchAppointments();
      setShowEditModal(false);
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
    }
  };

  // Fonction pour gérer les clics sur les plages à combler
  const handleUnassignedAppointmentClick = (appointment, event) => {
    console.log("handleUnassignedAppointmentClick appelé", appointment.status);
    event.preventDefault();
    event.stopPropagation();

    if (appointment.status === 'libre' && (hasPermission('write') || hasPermission('read'))) {
      console.log("Ouverture modal réservation");
      setSelectedUnassignedAppointment(appointment);
      setShowBookingModal(true);
    } else if (appointment.status === 'reserve') {
      console.log("Ouverture modal information");
      setSelectedInfoAppointment(appointment);
      setShowInfoModal(true);
    }
  };

  const handleBookAppointment = async () => {
    if (!selectedUnassignedAppointment || !currentUser) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedUnassignedAppointment.id);
      await updateDoc(appointmentRef, {
        employeeId: currentUser.userId,
        status: 'reserve'
      });

      fetchAppointments();
      setShowBookingModal(false);
    } catch (error) {
      console.error("Erreur lors de la réservation du rendez-vous:", error);
    }
  };

  const handleApproveAppointment = async () => {
    if (!selectedInfoAppointment || !hasPermission('write')) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'assigne'
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors de l'approbation du rendez-vous:", error);
    }
  };

  const handleRejectAppointment = async () => {
    if (!selectedInfoAppointment || !hasPermission('write')) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors du refus du rendez-vous:", error);
    }
  };

  const handleCancelReservation = async () => {
    if (!selectedInfoAppointment || selectedInfoAppointment.employeeId !== currentUser.userId) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors de l'annulation de la réservation:", error);
    }
  };

  return (
    <>
      <div className={`month-view-mobile flex flex-col h-[calc(100vh-260px)] ${theme === 'dark' ? 'dark' : ''} relative`}>
        <MobileMonthCalendar />
        <MobileDayAppointments />
      </div>


      {showDetailsModal && selectedDetailAppointment && (
        <EnhancedAppointmentDetailsModal
          appointment={selectedDetailAppointment}
          onClose={() => setShowDetailsModal(false)}
          onUpdate={(updatedAppointment) => {
            // Rafraîchir la liste des rendez-vous après mise à jour (pointage ou clôture)
            fetchAppointments();
            setShowDetailsModal(false);
          }}
          db={db}
        />
      )}

      {showEditModal && selectedEditAppointment && (
        <EnhancedEditAppointmentModal
          appointment={selectedEditAppointment}
          employees={employees}
          clients={clients}
          onSave={handleUpdateAppointment}
          onClose={() => setShowEditModal(false)}
          onDelete={handleDeleteAppointment}
          canDelete={hasPermission('delete')}
        />
      )}

      {/* Modal pour la réservation de plages à combler */}
      {showBookingModal && selectedUnassignedAppointment && (
        <EnhancedModal onClose={() => setShowBookingModal(false)} size="md">
          <ModalHeader>Réserver ce rendez-vous</ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <p className="text-gray-600 dark:text-gray-300">
                Voulez-vous réserver ce rendez-vous ?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Client :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.clientName || "Client non spécifié"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.date.toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.startTime} - {selectedUnassignedAppointment.endTime}</span>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalButton variant="secondary" onClick={() => setShowBookingModal(false)}>
              Annuler
            </ModalButton>
            <ModalButton variant="primary" onClick={handleBookAppointment}>
              Réserver
            </ModalButton>
          </ModalFooter>
        </EnhancedModal>
      )}

      {/* Modal d'information pour les rendez-vous en attente de validation */}
      {showInfoModal && selectedInfoAppointment && (
        <EnhancedModal onClose={() => setShowInfoModal(false)} size="md">
          <ModalHeader>Détails du rendez-vous</ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Client :</span>
                  <span>{selectedInfoAppointment.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                  <span>{selectedInfoAppointment.date.toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                  <span>{selectedInfoAppointment.startTime} - {selectedInfoAppointment.endTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Employé :</span>
                  <span>{getEmployeeName(selectedInfoAppointment.employeeId, employees)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Statut :</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedInfoAppointment.status === 'reserve'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                    {selectedInfoAppointment.status === 'reserve' ? 'En attente de validation' : selectedInfoAppointment.status}
                  </span>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            {selectedInfoAppointment.status === 'reserve' && (
              <>
                {hasPermission('write') && (
                  <>
                    <ModalButton variant="success" onClick={handleApproveAppointment}>
                      Approuver
                    </ModalButton>
                    <ModalButton variant="danger" onClick={handleRejectAppointment}>
                      Refuser
                    </ModalButton>
                  </>
                )}
                {selectedInfoAppointment.employeeId === currentUser.userId && (
                  <ModalButton variant="warning" onClick={handleCancelReservation}>
                    Annuler
                  </ModalButton>
                )}
              </>
            )}
            <ModalButton variant="secondary" onClick={() => setShowInfoModal(false)}>
              Fermer
            </ModalButton>
          </ModalFooter>
        </EnhancedModal>
      )}
    </>
  );
};


// Fonction pour obtenir le nom d'un employé
const getEmployeeName = (employeeId, employees) => {
  const employee = employees.find(emp => emp.userId === employeeId);
  return employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Employé non trouvé';
};

// Fonction pour éclaircir une couleur
const lightenColor = (color, percent) => {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (
    0x1000000 +
    (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)
  ).toString(16).slice(1);
};

// Fonction pour calculer la luminosité d'une couleur
const getBrightness = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
};

// Fonction pour déterminer la couleur du texte en fonction du fond
const getTextColor = (backgroundColor) => {
  const brightness = getBrightness(backgroundColor);
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

// Fonction utilitaire pour déterminer si une couleur est claire ou foncée
const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155;
};

// Composant pour la carte de rendez-vous mobile (style WeekViewMobile)
const MobileAppointmentCard = ({ appointment, employees, onAppointmentClick }) => {
  const { theme } = useTheme();

  let statusClass, statusText, statusIcon;

  switch (appointment.status) {
    case 'libre':
      statusClass = 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      statusText = 'À combler';
      statusIcon = '⚠️';
      break;
    case 'reserve':
      statusClass = 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      statusText = 'En attente';
      statusIcon = '⏳';
      break;
    case 'in_progress':
      statusClass = 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      statusText = 'En cours';
      statusIcon = '▶️';
      break;
    case 'completed':
      statusClass = 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      statusText = 'Terminé';
      statusIcon = '✅';
      break;
    default:
      statusClass = 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
      statusText = appointment.status || 'Assigné';
      statusIcon = '📅';
  }

  // Déterminer la couleur de la bordure gauche
  const borderColor = appointment.clientColor || (appointment.status === 'libre' ? '#ef4444' : '#6b7280');

  // Style pour l'indicateur de temps
  const timeStyle = "bg-gray-50 dark:bg-white/5 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5";

  return (
    <div
      className={`group relative mb-2 rounded-xl bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-md transition-all duration-200 active:scale-[0.98] overflow-hidden ${appointment.status === 'completed' ? 'opacity-75' : ''}`}
      style={{
        borderLeftWidth: '4px',
        borderLeftColor: borderColor
      }}
      onClick={onAppointmentClick}
    >
      <div className="p-3">
        {/* En-tête : Client et Statut */}
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2 overflow-hidden pr-2">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-50 dark:bg-gray-700 grid place-items-center text-gray-500 dark:text-gray-400">
              <User size={12} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-xs text-gray-900 dark:text-gray-100 truncate leading-tight">
                {appointment.clientName || "Client non spécifié"}
              </h4>
              {appointment.clientAddress && typeof appointment.clientAddress === 'string' && (
                <p className="text-[9px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {appointment.clientAddress.split(',')[0]}
                </p>
              )}
            </div>
          </div>

          {statusIcon && (
            <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 ${statusClass}`}>
              {statusIcon}
            </span>
          )}
        </div>

        {/* Corps : Horaire */}
        <div className="flex items-center justify-between mb-1">
          <div className={timeStyle}>
            <Clock size={10} className="text-gray-400" />
            <span>{appointment.startTime} - {appointment.endTime}</span>
          </div>
        </div>

        {/* Employé (si assigné) */}
        {appointment.employeeId && (
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 mt-1.5 px-0.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
            <span className="truncate max-w-[150px]">{getEmployeeName(appointment.employeeId, employees)}</span>
          </div>
        )}

        {/* Footer : Notes et Check-in */}
        {((appointment.notes && appointment.notes.content) || (appointment.checkInStatus)) && (
          <div className="flex gap-2 mt-2 pt-1.5 border-t border-gray-100 dark:border-gray-700/50">
            {(appointment.notes && appointment.notes.content) && (
              <span className="block w-2 h-2 rounded-full bg-indigo-500" title="Notes"></span>
            )}
            {appointment.checkInStatus && appointment.checkInStatus.checkInTime && (
              <Check size={10} className="text-emerald-500" strokeWidth={3} title="Pointé" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthViewMobile;
